'use client';

import { Children, useEffect, useRef, useState, useTransition, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import Link from 'next/link';
import { Loader2, CheckCircle2, Clock, UserCheck, ArrowLeft, Upload, FileText, X } from 'lucide-react';

import { DetailsDrawer } from './details-drawer';
import { IntakeTourField, type TourSelection } from './intake-tour-field';
import { NativePurchaseMembershipCard } from '../memberships/_components/native-purchase-membership-card';
import { selectMembershipPlanCopy } from '../memberships/_lib/select-plan-copy';

import { Switch } from '@kit/ui/switch';
import { PhoneInput } from '@kit/ui/phone-input';
import { Trans } from '@kit/ui/trans';
import { CaptchaField } from '@kit/auth/captcha/client';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@kit/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@kit/ui/select';

import authConfig from '~/config/auth.config';

import { useNativeTokens } from '~/(tenant)/_components/native/_lib/use-native-tokens';
import { useNativePresentation } from '~/(tenant)/_components/native/_lib/use-native-presentation';
import {
  DirectionalArrow,
  Eyebrow,
  NativePageShell,
} from '~/(tenant)/_components/native/_primitives';

import { isValidPhoneNumber } from '@kit/ui/phone-input';

import {
  IntakeFormBaseSchema,
  type IntakePlan,
  type IntakeCustomField,
} from '../_lib/schema/intake.schema';
import type { IntakeResult } from '../_lib/server/intake-actions';
import {
  checkMembershipStatusAction,
  submitMembershipIntakeAction,
} from '../_lib/server/intake-actions';
import { createIntakeFileUploadUrl } from '../_lib/server/custom-field-file-actions';
import { ALLOWED_MIME_TYPES } from '../_lib/schema/allowed-mime-types';
import { useSupabase } from '@kit/supabase/hooks/use-supabase';

const hasCaptcha = !!authConfig.captchaTokenSiteKey;

// Validation messages are i18n keys resolved at the display site (the phone
// field passes fieldState.error?.message through t()). NativeInputRow renders
// the raw message string, so keying here + translating at render keeps the
// error localized without threading translations into the module-scope schema.
const PhoneStepSchema = z.object({
  phoneNumber: z.string().min(1, 'marketing:validation.phoneRequired').refine(
    (val) => isValidPhoneNumber(val),
    { message: 'marketing:validation.phoneInvalid' },
  ),
});

const IntakeFormWithCaptchaSchema = IntakeFormBaseSchema.extend({
  phoneNumber: z.string().min(1, 'marketing:validation.phoneRequired').refine(
    (val) => isValidPhoneNumber(val),
    { message: 'marketing:validation.phoneInvalid' },
  ),
  captchaToken: hasCaptcha ? z.string().min(1) : z.string().optional().default(''),
});

type IntakeFormWithCaptchaData = z.infer<typeof IntakeFormWithCaptchaSchema>;

/* -------------------------------------------------------------------------- */
/* Native primitives — local to this file. Mirrors the iOS/editorial idiom    */
/* used by /packages and /memberships so the surfaces feel like one product.  */
/* See useNativeTokens() for the token system driving tenant colors.          */
/* -------------------------------------------------------------------------- */

/**
 * iOS UITableView grouped-inset input container. Mirrors @kit/auth/native's
 * NativeGroupedInput but is local so we don't need to export auth internals
 * for a single consumer. Auto-adapts dividers + tint to dark/light surface.
 */
function NativeGroupedInput({ children }: { children: ReactNode }) {
  const t = useNativeTokens();
  const items = Children.toArray(children).filter(Boolean);
  const isDark = isDarkBg(t.background);
  const rowBg = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.035)';
  const dividerColor = isDark
    ? 'rgba(255,255,255,0.08)'
    : 'rgba(0,0,0,0.06)';

  return (
    <div
      style={{
        overflow: 'hidden',
        borderRadius: t.radius,
        background: rowBg,
      }}
    >
      {items.map((child, index) => (
        <div key={index}>
          {index > 0 ? (
            <div
              style={{
                marginInlineStart: 16,
                marginInlineEnd: 16,
                borderTop: `1px solid ${dividerColor}`,
              }}
            />
          ) : null}
          <div style={{ padding: '14px 16px' }}>{child}</div>
        </div>
      ))}
    </div>
  );
}

/**
 * Single row inside NativeGroupedInput. Renders an iOS-style caption label
 * stacked above the control. The control itself is borderless so the only
 * visible chrome is the rounded grouped container around it.
 */
function NativeInputRow({
  label,
  description,
  children,
  error,
  /**
   * 'caption' = small caps mono label (~12px). Used for the built-in
   * name/email/phone rows so the existing visual rhythm is preserved.
   * 'title' = larger sentence-case label (~14px). Used for dynamic
   * custom fields where the field's own label is the title of the row
   * and must be readable enough to stand alone.
   */
  labelVariant = 'caption',
}: {
  label?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  error?: ReactNode;
  labelVariant?: 'caption' | 'title';
}) {
  const t = useNativeTokens();
  const hasError = Boolean(error);
  const captionStyle: React.CSSProperties = {
    fontFamily: t.fontMono,
    fontSize: 12,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: hasError ? '#B25040' : t.muted,
  };
  const titleStyle: React.CSSProperties = {
    fontFamily: t.fontSans,
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1.3,
    color: hasError ? '#B25040' : t.foreground,
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {label ? (
        <span style={labelVariant === 'title' ? titleStyle : captionStyle}>
          {label}
        </span>
      ) : null}
      {description ? (
        <span
          style={{
            fontFamily: t.fontSans,
            fontSize: 12,
            color: t.muted,
            lineHeight: 1.4,
          }}
        >
          {description}
        </span>
      ) : null}
      {children}
      {error ? (
        <span
          style={{
            fontFamily: t.fontSans,
            fontSize: 12,
            color: '#B25040',
            marginTop: 2,
          }}
        >
          {error}
        </span>
      ) : null}
    </div>
  );
}

/**
 * Native-feeling text input that lives inside NativeInputRow. Borderless,
 * transparent — visual chrome comes from the parent grouped container.
 */
function NativeTextInput({
  value,
  onChange,
  onBlur,
  disabled,
  placeholder,
  type = 'text',
  inputMode,
  autoComplete,
  name,
}: {
  value?: string | number;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  placeholder?: string;
  type?: string;
  inputMode?: 'text' | 'email' | 'tel' | 'numeric' | 'decimal';
  autoComplete?: string;
  name?: string;
}) {
  const t = useNativeTokens();
  return (
    <input
      type={type}
      name={name}
      value={value ?? ''}
      onChange={onChange}
      onBlur={onBlur}
      disabled={disabled}
      placeholder={placeholder}
      inputMode={inputMode}
      autoComplete={autoComplete}
      style={{
        width: '100%',
        background: 'transparent',
        border: 'none',
        outline: 'none',
        padding: 0,
        fontFamily: t.fontSans,
        fontSize: 16,
        color: t.foreground,
        opacity: disabled ? 0.55 : 1,
      }}
      dir="auto"
    />
  );
}

/**
 * Full-width tenant-tinted CTA. Mirrors the "Become a Member" pill on the
 * purchase card so all buttons across the membership surfaces have the same
 * silhouette.
 */
function NativeAuthButton({
  children,
  type = 'button',
  onClick,
  disabled,
  loading,
  variant = 'primary',
}: {
  children: ReactNode;
  type?: 'button' | 'submit';
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  variant?: 'primary' | 'ghost';
}) {
  const t = useNativeTokens();
  const isGhost = variant === 'ghost';
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      style={{
        width: '100%',
        padding: '15px 0',
        background: isGhost ? 'transparent' : t.accent,
        color: isGhost ? t.foreground : t.background,
        border: isGhost ? `1px solid ${t.foreground}33` : 'none',
        borderRadius: 'var(--native-booking-control-radius, 12px)',
        fontFamily: t.fontSans,
        fontSize: 11,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        fontWeight: 500,
        cursor: disabled || loading ? 'not-allowed' : 'pointer',
        opacity: disabled || loading ? 0.5 : 1,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
      }}
    >
      {loading ? <Loader2 size={14} className="animate-spin" /> : null}
      {children}
    </button>
  );
}

function isDarkBg(color: string): boolean {
  const hex = color.replace('#', '');
  if (hex.length !== 6) return false;
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.5;
}

/**
 * Small back-affordance shown above the form when the user can return to the
 * phone step. Mirrors the editorial typography rather than shadcn's button.
 */
function BackLink({
  onClick,
  children,
}: {
  onClick: () => void;
  children: ReactNode;
}) {
  const t = useNativeTokens();
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        background: 'transparent',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        fontFamily: t.fontMono,
        fontSize: 10,
        letterSpacing: '0.18em',
        textTransform: 'uppercase',
        color: t.muted,
      }}
    >
      <ArrowLeft size={12} strokeWidth={1.6} />
      {children}
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/* End native primitives                                                      */
/* -------------------------------------------------------------------------- */

interface MembershipIntakeFormProps {
  plans: IntakePlan[];
  customFields: IntakeCustomField[];
  businessName: string | null;
  membershipIntro: string | null;
  loggedInUser?: { name: string; phoneNumber: string; email: string; membershipStatus: 'active' | 'pending' | null } | null;
}

type Step =
  | { type: 'intro' }
  | { type: 'phone' }
  | { type: 'form'; phoneNumber: string }
  // isAlreadyLoggedIn: true when we reached this state from server-side user check
  // (user is authenticated). false when reached via the phone-check flow.
  | { type: 'existing'; status: 'active' | 'pending'; isAlreadyLoggedIn?: boolean }
  | { type: 'submitted'; planName: string };

export function MembershipIntakeForm({
  plans,
  customFields,
  businessName: _businessName,
  membershipIntro,
  loggedInUser,
}: MembershipIntakeFormProps) {
  const hasIntro = Boolean(membershipIntro);
  // If user is logged in, check membership status first
  const initialStep: Step = loggedInUser?.membershipStatus
    ? { type: 'existing', status: loggedInUser.membershipStatus, isAlreadyLoggedIn: true }
    : loggedInUser
      ? { type: 'form', phoneNumber: loggedInUser.phoneNumber }
      : hasIntro
        ? { type: 'intro' }
        : { type: 'phone' };
  const [step, setStep] = useState<Step>(initialStep);

  if (plans.length === 0) {
    return (
      <NativePageShell
        eyebrow={
          <Trans
            i18nKey="marketing:joinMembership"
            defaults="Become a member"
          />
        }
        title={
          <Trans
            i18nKey="marketing:noPlansAvailable"
            defaults="No plans available"
          />
        }
      >
        <CenteredBody>
          <Trans
            i18nKey="marketing:noPlansDescription"
            defaults="This venue does not currently have any membership plans available."
          />
        </CenteredBody>
      </NativePageShell>
    );
  }

  if (step.type === 'submitted') {
    return (
      <NativePageShell
        eyebrow={
          <Trans
            i18nKey="marketing:intakeSuccess"
            defaults="Application submitted"
          />
        }
        title={
          <Trans
            i18nKey="marketing:intakeSuccessTitle"
            defaults="You're on the list"
          />
        }
      >
        <StatusBlock
          icon={<CheckCircle2 size={36} strokeWidth={1.4} />}
          accentColor="#3F8F4F"
          body={
            <Trans
              i18nKey="marketing:intakeSuccessDescription"
              values={{ planName: step.planName }}
              defaults="We received your application for {{planName}}. We'll be in touch soon."
            />
          }
        />
      </NativePageShell>
    );
  }

  if (step.type === 'existing') {
    const isActive = step.status === 'active';
    // When the user arrived at this screen because they are already authenticated
    // (server-side check), there is no point offering a "Sign In" button — they
    // are already signed in. Also hide "Use a Different Number" since it would
    // bypass their authenticated session in a confusing way.
    const showSignIn = isActive && !step.isAlreadyLoggedIn;
    const showUseDifferentNumber = !step.isAlreadyLoggedIn;

    return (
      <NativePageShell
        eyebrow={
          <Trans
            i18nKey={
              isActive
                ? 'marketing:membershipAlreadyActive'
                : 'marketing:membershipPending'
            }
            defaults={
              isActive ? "You're already a member" : 'Application under review'
            }
          />
        }
        title={
          isActive ? (
            <Trans
              i18nKey="marketing:membershipAlreadyActiveTitle"
              defaults="Welcome back"
            />
          ) : (
            <Trans
              i18nKey="marketing:membershipPendingTitle"
              defaults="Thanks for applying"
            />
          )
        }
      >
        <StatusBlock
          icon={
            isActive ? (
              <UserCheck size={36} strokeWidth={1.4} />
            ) : (
              <Clock size={36} strokeWidth={1.4} />
            )
          }
          accentColor={isActive ? '#3F8F4F' : '#B68628'}
          body={
            <Trans
              i18nKey={
                isActive
                  ? 'marketing:membershipAlreadyActiveDescription'
                  : 'marketing:membershipPendingDescription'
              }
              defaults={
                isActive
                  ? 'Your membership is currently active.'
                  : "Your application is being reviewed. We'll get back to you soon."
              }
            />
          }
        />

        <div
          style={{
            marginTop: 28,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          {showSignIn && (
            <Link href="/auth/sign-in" style={{ textDecoration: 'none' }}>
              <NativeAuthButton type="button">
                <Trans i18nKey="auth:signIn" defaults="Sign In" />
              </NativeAuthButton>
            </Link>
          )}

          {showUseDifferentNumber && (
            <NativeAuthButton
              variant="ghost"
              onClick={() => setStep({ type: 'phone' })}
            >
              <Trans
                i18nKey="marketing:useAnotherNumber"
                defaults="Use a different number"
              />
            </NativeAuthButton>
          )}
        </div>
      </NativePageShell>
    );
  }

  if (step.type === 'intro') {
    return (
      <MembershipIntroStep
        intro={membershipIntro!}
        onNext={() => setStep({ type: 'phone' })}
      />
    );
  }

  if (step.type === 'phone') {
    return (
      <PhoneStep
        onVerified={(phoneNumber, membershipStatus) => {
          if (membershipStatus) {
            setStep({ type: 'existing', status: membershipStatus });
          } else {
            setStep({ type: 'form', phoneNumber });
          }
        }}
      />
    );
  }

  return (
    <ApplicationForm
      phoneNumber={step.phoneNumber}
      plans={plans}
      customFields={customFields}
      loggedInUser={loggedInUser}
      onBack={() => setStep({ type: 'phone' })}
      onSubmitted={(planName) => setStep({ type: 'submitted', planName })}
    />
  );
}

/**
 * Centered status block (icon + body copy) used by the submitted + existing
 * screens. Replaces the old "lucide icon in a green circle" pattern with the
 * editorial equivalent — thin-stroke icon in a tinted ring on a quiet field.
 */
function StatusBlock({
  icon,
  accentColor,
  body,
}: {
  icon: ReactNode;
  accentColor: string;
  body: ReactNode;
}) {
  return (
    <div style={{ textAlign: 'center', padding: '24px 0' }}>
      <div
        aria-hidden
        style={{
          margin: '0 auto 18px',
          width: 72,
          height: 72,
          borderRadius: 999,
          background: `${accentColor}1A`,
          color: accentColor,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {icon}
      </div>
      <CenteredBody>{body}</CenteredBody>
    </div>
  );
}

function CenteredBody({ children }: { children: ReactNode }) {
  const t = useNativeTokens();
  return (
    <p
      style={{
        margin: 0,
        fontFamily: t.fontSans,
        fontSize: 14,
        color: t.muted,
        lineHeight: 1.6,
        maxWidth: 320,
        marginLeft: 'auto',
        marginRight: 'auto',
      }}
    >
      {children}
    </p>
  );
}

/**
 * Renders text with basic **bold** markdown support.
 * Splits on ** markers and alternates between normal and bold spans.
 */
function renderBoldMarkdown(text: string) {
  const parts = text.split(/\*\*(.*?)\*\*/g);

  return parts.map((part, i) =>
    i % 2 === 1 ? <strong key={i}>{part}</strong> : part,
  );
}

/**
 * Intro step: Shows membership intro text before the phone step
 */
function MembershipIntroStep({
  intro,
  onNext,
}: {
  intro: string;
  onNext: () => void;
}) {
  const t = useNativeTokens();
  return (
    <NativePageShell
      eyebrow={
        <Trans i18nKey="marketing:joinMembership" defaults="Become a member" />
      }
      title={<Trans i18nKey="marketing:welcomeTitle" defaults="Welcome" />}
    >
      <div
        style={{
          whiteSpace: 'pre-line',
          fontFamily: t.fontSans,
          fontSize: 14,
          color: t.muted,
          lineHeight: 1.65,
          marginBottom: 28,
        }}
        dir="auto"
      >
        {renderBoldMarkdown(intro)}
      </div>

      <NativeAuthButton type="button" onClick={onNext}>
        <Trans i18nKey="common:continue" defaults="Continue" />
        <DirectionalArrow size={12} strokeWidth={1.4} />
      </NativeAuthButton>
    </NativePageShell>
  );
}

/**
 * Step 1: Phone number entry + membership check
 */
function PhoneStep({
  onVerified,
}: {
  onVerified: (
    phoneNumber: string,
    membershipStatus: 'active' | 'pending' | null,
  ) => void;
}) {
  const { t } = useTranslation();
  const [isChecking, startChecking] = useTransition();

  const form = useForm({
    resolver: zodResolver(PhoneStepSchema),
    defaultValues: { phoneNumber: '' },
  });

  const onSubmit = (data: z.infer<typeof PhoneStepSchema>) => {
    startChecking(async () => {
      try {
        const result = await checkMembershipStatusAction({
          phoneNumber: data.phoneNumber,
        });

        if (result.hasMembership) {
          onVerified(data.phoneNumber, result.status);
        } else {
          onVerified(data.phoneNumber, null);
        }
      } catch {
        // If check fails, proceed to form anyway
        onVerified(data.phoneNumber, null);
      }
    });
  };

  return (
    <NativePageShell
      eyebrow={
        <Trans i18nKey="marketing:joinMembership" defaults="Become a member" />
      }
      title={
        <Trans
          i18nKey="marketing:phoneStepTitle"
          defaults="What's your number?"
        />
      }
    >
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          style={{ display: 'flex', flexDirection: 'column', gap: 18 }}
        >
          <FormField
            control={form.control}
            name="phoneNumber"
            render={({ field, fieldState }) => (
              <FormItem>
                <NativeGroupedInput>
                  <NativeInputRow
                    label={
                      <Trans
                        i18nKey="common:phoneNumber"
                        defaults="Phone Number"
                      />
                    }
                    error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                  >
                    <FormControl>
                      <PhoneInput
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        disabled={isChecking}
                      />
                    </FormControl>
                  </NativeInputRow>
                </NativeGroupedInput>
                {/* FormMessage suppressed — NativeInputRow surfaces the error
                    inline with the row label. */}
                <FormMessage className="sr-only" />
              </FormItem>
            )}
          />

          <NativeAuthButton type="submit" loading={isChecking}>
            {isChecking ? (
              <Trans i18nKey="common:loading" defaults="Loading..." />
            ) : (
              <>
                <Trans i18nKey="common:continue" defaults="Continue" />
                <DirectionalArrow size={12} strokeWidth={1.4} />
              </>
            )}
          </NativeAuthButton>
        </form>
      </Form>
    </NativePageShell>
  );
}

/**
 * Step 2: Full application form (phone already verified)
 */
function ApplicationForm({
  phoneNumber,
  plans,
  customFields,
  loggedInUser,
  onBack,
  onSubmitted,
}: {
  phoneNumber: string;
  plans: IntakePlan[];
  customFields: IntakeCustomField[];
  loggedInUser?: { name: string; phoneNumber: string; email: string; membershipStatus: 'active' | 'pending' | null } | null;
  onBack: () => void;
  onSubmitted: (planName: string) => void;
}) {
  const supabase = useSupabase();
  const { i18n, t } = useTranslation();
  const [isPending, startTransition] = useTransition();
  const [detailsPlanId, setDetailsPlanId] = useState<string | null>(null);
  const pendingFiles = useRef<Map<string, File>>(new Map());
  const singlePlan = plans.length === 1;
  const isPreFilled = !!loggedInUser;

  const form = useForm<IntakeFormWithCaptchaData>({
    resolver: zodResolver(IntakeFormWithCaptchaSchema),
    defaultValues: {
      planId: singlePlan ? plans[0]!.id : '',
      name: loggedInUser?.name ?? '',
      phoneNumber,
      email: loggedInUser?.email ?? '',
      customFields: [],
      captchaToken: '',
    },
  });

  const selectedPlanId = form.watch('planId');
  const captchaToken = form.watch('captchaToken');
  const captchaLoading = hasCaptcha && !captchaToken;

  // Scroll the "About you" section into view the moment a plan is picked, so
  // the next input (name) is on screen without the user reaching for it.
  // Fires once per unique plan transition — re-selecting the same plan
  // consecutively does not re-scroll, but toggling between plans (A → B → A)
  // does fire on each switch. Skipped for single-plan and pre-filled forms
  // since the user is already past the picker.
  const aboutYouRef = useRef<HTMLElement | null>(null);
  const lastScrolledPlanRef = useRef<string | null>(null);
  useEffect(() => {
    if (singlePlan || isPreFilled) return;
    if (!selectedPlanId) return;
    if (lastScrolledPlanRef.current === selectedPlanId) return;
    lastScrolledPlanRef.current = selectedPlanId;
    // Defer until the next frame so the radio's selected-state paint lands
    // first — otherwise the scroll fires before the visual change registers
    // and the move feels disconnected from the tap.
    requestAnimationFrame(() => {
      aboutYouRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  }, [selectedPlanId, singlePlan, isPreFilled]);

  const onSubmit = (data: IntakeFormWithCaptchaData) => {
    let customFieldValues = (data.customFields ?? []).filter(
      (cf): cf is NonNullable<typeof cf> => cf != null && cf.value != null,
    );

    // Validate required custom fields (treat pending file uploads as filled).
    // Each missing required field gets its own RHF error so the row's label
    // glows red inline, matching how zod-driven errors render on Name/etc.
    // The toast is kept as a summary so the user knows something is off even
    // if the missing field is below the fold.
    const filledFieldIds = new Set(customFieldValues.map((cf) => cf.fieldId));
    const missingRequired = customFields
      .map((f, idx) => ({ field: f, idx }))
      .filter(
        ({ field: f }) => f.is_required && !filledFieldIds.has(f.id),
      );

    if (missingRequired.length > 0) {
      const requiredMsg = t('marketing:fieldRequired', {
        defaultValue: 'This field is required',
      });
      for (const { idx } of missingRequired) {
        form.setError(`customFields.${idx}`, {
          type: 'required',
          message: requiredMsg,
        });
      }
      toast.error(
        t('marketing:missingFieldsList', {
          fields: missingRequired.map(({ field: f }) => f.label).join(', '),
          defaultValue: 'Please fill in: {{fields}}',
        }),
      );
      return;
    }

    startTransition(async () => {
      try {
        // Upload pending files directly to Supabase Storage via signed URLs
        // (bypasses the 1MB server action body limit on Vercel)
        if (pendingFiles.current.size > 0) {
          for (const [fieldId, file] of pendingFiles.current) {
            // 1. Get a signed upload URL from the server (no file data sent)
            const urlResult = await createIntakeFileUploadUrl({
              customerId: data.phoneNumber,
              fieldId,
              fileName: file.name,
            });

            if (!urlResult.success) {
              toast.error(urlResult.error || t('marketing:intake.uploadPrepareFailed'));
              return;
            }

            // 2. Upload the file directly to Supabase Storage from the client
            const { error: uploadError } = await supabase.storage
              .from('customer_documents')
              .uploadToSignedUrl(urlResult.path, urlResult.token, file, {
                upsert: true,
              });

            if (uploadError) {
              toast.error(t('marketing:intake.uploadFailed'));
              return;
            }

            // Replace the "pending" placeholder with the actual storage path so
            // the submitted payload references the uploaded file.
            // cfIndex < 0 means the file was staged but the field was later removed
            // from customFieldValues (e.g., user cleared a required field). The file
            // will still be uploaded (minor orphaned object), but it won't be
            // submitted — the "pending" filter below will drop it anyway.
            const cfIndex = customFieldValues.findIndex((cf) => cf.fieldId === fieldId);
            if (cfIndex >= 0) {
              customFieldValues[cfIndex] = {
                fieldId,
                value: { file: urlResult.path },
              };
            }
          }

          // Only clear the map after all uploads succeed so that a partial failure
          // leaves the files staged for a retry without the user re-selecting them.
          pendingFiles.current.clear();
        }

        // Drop any values that are still "pending" — these represent file fields
        // where the upload was skipped (e.g., not required, no file selected).
        customFieldValues = customFieldValues.filter(
          (cf) => !('file' in cf.value && cf.value.file === 'pending'),
        );

        const result = await submitMembershipIntakeAction({
          ...data,
          customFields: customFieldValues,
        }) as IntakeResult;

        if (result.success && result.data) {
          onSubmitted(result.data.planName);
        } else if (!result.success) {
          toast.error(result.error || t('marketing:intake.submitFailed'));
        }
      } catch (e) {
        toast.error(e instanceof Error ? e.message : t('marketing:intake.submitFailed'));
      }
    });
  };

  return (
    <NativePageShell
      eyebrow={
        <Trans
          i18nKey="marketing:joinMembership"
          defaults="Become a member"
        />
      }
      title={
        <Trans
          i18nKey="marketing:applicationTitle"
          defaults="Your details"
        />
      }
    >
      {!isPreFilled && (
        <div style={{ marginBottom: 18 }}>
          <BackLink onClick={onBack}>
            <span dir="ltr">{phoneNumber}</span>
          </BackLink>
        </div>
      )}

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          style={{ display: 'flex', flexDirection: 'column', gap: 22 }}
        >
          {/* Plan Selection (skip if single plan). Reuses the same purchase
              card as /memberships so the picker is visually identical to the
              marketing surface — selectable mode swaps the bottom CTA for a
              Selected/Tap-to-select pill. */}
          {!singlePlan && (
            <section
              role="radiogroup"
              aria-label={t('marketing:selectPlan', { defaultValue: 'Membership plan' })}
              aria-required="true"
              style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
            >
              <Eyebrow size="md" style={{ marginBottom: 8 }}>
                <Trans
                  i18nKey="marketing:selectPlan"
                  defaults="Select a plan"
                />
              </Eyebrow>
              {plans.map((plan) => (
                <NativePurchaseMembershipCard
                  key={plan.id}
                  plan={plan}
                  selectable
                  selected={selectedPlanId === plan.id}
                  onSelect={() =>
                    form.setValue('planId', plan.id, { shouldValidate: true })
                  }
                />
              ))}
              {form.formState.errors.planId && (
                <p
                  style={{
                    margin: 0,
                    fontFamily: 'inherit',
                    fontSize: 12,
                    color: '#B25040',
                  }}
                >
                  <Trans
                    i18nKey="marketing:selectPlanRequired"
                    defaults="Please select a membership plan"
                  />
                </p>
              )}
            </section>
          )}

          {/* Personal Information — grouped iOS panel */}
          <section
            ref={aboutYouRef}
            style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
          >
            <Eyebrow size="sm" style={{ marginBottom: 4 }}>
              <Trans
                i18nKey="marketing:yourDetailsHeading"
                defaults="About you"
              />
            </Eyebrow>
            <NativeGroupedInput>
              <FormField
                control={form.control}
                name="name"
                render={({ field, fieldState }) => (
                  <NativeInputRow
                    label={
                      <>
                        <Trans i18nKey="common:name" defaults="Name" /> *
                      </>
                    }
                    error={fieldState.error?.message}
                  >
                    <FormControl>
                      <NativeTextInput
                        name={field.name}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        placeholder={t('common:fullNamePlaceholder')}
                        autoComplete="name"
                        disabled={isPending || isPreFilled}
                      />
                    </FormControl>
                  </NativeInputRow>
                )}
              />

              {isPreFilled && (
                <FormField
                  control={form.control}
                  name="phoneNumber"
                  render={({ field }) => (
                    <NativeInputRow
                      label={
                        <Trans
                          i18nKey="common:phoneNumber"
                          defaults="Phone Number"
                        />
                      }
                    >
                      <FormControl>
                        <NativeTextInput
                          name={field.name}
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          disabled
                        />
                      </FormControl>
                    </NativeInputRow>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="email"
                render={({ field, fieldState }) => (
                  <NativeInputRow
                    label={
                      <Trans
                        i18nKey="common:emailAddress"
                        defaults="Email Address"
                      />
                    }
                    error={fieldState.error?.message}
                  >
                    <FormControl>
                      <NativeTextInput
                        type="email"
                        name={field.name}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        placeholder={t('common:emailPlaceholder')}
                        autoComplete="email"
                        inputMode="email"
                        disabled={isPending || isPreFilled}
                      />
                    </FormControl>
                  </NativeInputRow>
                )}
              />
            </NativeGroupedInput>
          </section>

          {/* Dynamic Custom Fields — text/number/date/select/boolean live in
              the same grouped panel; file uploads render below as a dropzone. */}
          {customFields.length > 0 && (
            <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <Eyebrow size="sm" style={{ marginBottom: 4 }}>
                <Trans
                  i18nKey="marketing:additionalInfoHeading"
                  defaults="A bit more"
                />
              </Eyebrow>

              {(() => {
                const groupedFields = customFields.filter(
                  (f) => f.field_type !== 'file' && f.field_type !== 'tour',
                );
                const fileFields = customFields.filter(
                  (f) => f.field_type === 'file',
                );
                // Tour fields render their own calendar/guide widget, so they
                // sit outside the compact grouped panel (like file uploads).
                const tourFields = customFields.filter(
                  (f) => f.field_type === 'tour',
                );
                return (
                  <>
                    {groupedFields.length > 0 && (
                      <NativeGroupedInput>
                        {groupedFields.map((field) => {
                          const originalIndex = customFields.findIndex(
                            (f) => f.id === field.id,
                          );
                          return (
                            <DynamicField
                              key={field.id}
                              field={field}
                              index={originalIndex}
                              form={form}
                              disabled={isPending}
                              pendingFiles={pendingFiles}
                            />
                          );
                        })}
                      </NativeGroupedInput>
                    )}
                    {fileFields.map((field) => {
                      const originalIndex = customFields.findIndex(
                        (f) => f.id === field.id,
                      );
                      return (
                        <DynamicField
                          key={field.id}
                          field={field}
                          index={originalIndex}
                          form={form}
                          disabled={isPending}
                          pendingFiles={pendingFiles}
                        />
                      );
                    })}
                    {tourFields.map((field) => {
                      const originalIndex = customFields.findIndex(
                        (f) => f.id === field.id,
                      );
                      return (
                        <div
                          key={field.id}
                          className="rounded-2xl border bg-card p-4"
                        >
                          <DynamicField
                            field={field}
                            index={originalIndex}
                            form={form}
                            disabled={isPending}
                            pendingFiles={pendingFiles}
                          />
                        </div>
                      );
                    })}
                  </>
                );
              })()}
            </section>
          )}

          {hasCaptcha && (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <CaptchaField
                siteKey={authConfig.captchaTokenSiteKey}
                control={form.control}
                name="captchaToken"
              />
            </div>
          )}

          <NativeAuthButton
            type="submit"
            disabled={isPending || captchaLoading}
            loading={isPending}
          >
            {captchaLoading ? (
              <Trans
                i18nKey="auth:verifyingCaptcha"
                defaults="Verifying..."
              />
            ) : isPending ? (
              <Trans
                i18nKey="marketing:submitting"
                defaults="Submitting..."
              />
            ) : (
              <>
                <Trans
                  i18nKey="marketing:submitApplication"
                  defaults="Submit Application"
                />
                <DirectionalArrow size={12} strokeWidth={1.4} />
              </>
            )}
          </NativeAuthButton>
        </form>

        {/* Shared "view details" drawer for whichever plan card was tapped. */}
        <DetailsDrawer
          open={detailsPlanId !== null}
          onOpenChange={(next) => {
            if (!next) setDetailsPlanId(null);
          }}
          {...(() => {
            const plan = plans.find((p) => p.id === detailsPlanId);
            if (!plan) return { name: '' };
            const copy = selectMembershipPlanCopy(plan, i18n.language);
            return {
              name: plan.name,
              description: copy.description,
              features: copy.benefits,
              imageUrl: plan.image_url,
              color: plan.color,
            };
          })()}
        />
      </Form>
    </NativePageShell>
  );
}

/**
 * Renders a single custom field based on its field_type
 */
function DynamicField({
  field,
  index,
  form,
  disabled,
  pendingFiles,
}: {
  field: IntakeCustomField;
  index: number;
  form: ReturnType<typeof useForm<IntakeFormWithCaptchaData>>;
  disabled: boolean;
  pendingFiles: React.RefObject<Map<string, File>>;
}) {
  const t = useNativeTokens();
  const { t: translate } = useTranslation();
  const presentation = useNativePresentation();
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);

  const label = (
    <>
      {field.label}
      {field.is_required ? ' *' : ''}
    </>
  );
  // Plain-string variant used as the input placeholder for text/number/date
  // custom fields. The field label sits *inside* the input until the user
  // types — the description renders below in a smaller, secondary style.
  const placeholderLabel = `${field.label}${field.is_required ? ' *' : ''}`;
  const description = field.description ? field.description : undefined;

  // Each custom field renders inside <FormField> so it's driven by Controller —
  // the same pattern name/email use above. The previous implementation called
  // form.setValue without subscribing the row to a controller, so typed
  // characters never made it back into the input's value prop and the field
  // appeared "stuck".
  switch (field.field_type) {
    case 'text':
      return (
        <FormField
          control={form.control}
          name={`customFields.${index}`}
          render={({ field: rhfField, fieldState }) => {
            const stored = rhfField.value as
              | { value?: { text?: string } }
              | undefined;
            const value = stored?.value?.text ?? '';
            return (
              <NativeInputRow
                label={label}
                description={description}
                labelVariant="title"
                error={fieldState.error?.message}
              >
                <NativeTextInput
                  disabled={disabled}
                  value={value}
                  onChange={(e) => {
                    rhfField.onChange({
                      fieldId: field.id,
                      value: { text: e.target.value },
                    });
                    if (e.target.value) {
                      form.clearErrors(`customFields.${index}`);
                    }
                  }}
                  onBlur={rhfField.onBlur}
                />
              </NativeInputRow>
            );
          }}
        />
      );

    case 'number':
      return (
        <FormField
          control={form.control}
          name={`customFields.${index}`}
          render={({ field: rhfField, fieldState }) => {
            const stored = rhfField.value as
              | { value?: { number?: number } }
              | undefined;
            const n = stored?.value?.number;
            const value = typeof n === 'number' && Number.isFinite(n) ? String(n) : '';
            return (
              <NativeInputRow
                label={label}
                description={description}
                labelVariant="title"
                error={fieldState.error?.message}
              >
                <NativeTextInput
                  type="number"
                  inputMode="decimal"
                  disabled={disabled}
                  value={value}
                  onChange={(e) => {
                    // Empty input → valueAsNumber is NaN. Writing NaN to form
                    // state would bypass the required check (NaN passes the
                    // != null filter) and serialise to JSON null on the wire,
                    // so drop the row entirely when the input is blank.
                    if (e.target.value === '') {
                      form.resetField(`customFields.${index}`, {
                        defaultValue: undefined,
                      });
                      return;
                    }
                    const n = e.target.valueAsNumber;
                    if (!Number.isFinite(n)) return;
                    rhfField.onChange({
                      fieldId: field.id,
                      value: { number: n },
                    });
                    form.clearErrors(`customFields.${index}`);
                  }}
                  onBlur={rhfField.onBlur}
                />
              </NativeInputRow>
            );
          }}
        />
      );

    case 'date':
      return (
        <FormField
          control={form.control}
          name={`customFields.${index}`}
          render={({ field: rhfField, fieldState }) => {
            const stored = rhfField.value as
              | { value?: { date?: string } }
              | undefined;
            const value = stored?.value?.date ?? '';
            return (
              <NativeInputRow
                label={label}
                description={description}
                labelVariant="title"
                error={fieldState.error?.message}
              >
                <NativeTextInput
                  type="date"
                  disabled={disabled}
                  value={value}
                  onChange={(e) => {
                    rhfField.onChange({
                      fieldId: field.id,
                      value: { date: e.target.value },
                    });
                    if (e.target.value) {
                      form.clearErrors(`customFields.${index}`);
                    }
                  }}
                  onBlur={rhfField.onBlur}
                />
              </NativeInputRow>
            );
          }}
        />
      );

    case 'select':
      return (
        <FormField
          control={form.control}
          name={`customFields.${index}`}
          render={({ field: rhfField, fieldState }) => {
            const stored = rhfField.value as
              | { value?: { select?: string } }
              | undefined;
            const value = stored?.value?.select ?? '';
            return (
              <NativeInputRow
                label={label}
                description={description}
                labelVariant="title"
                error={fieldState.error?.message}
              >
                <Select
                  disabled={disabled}
                  value={value}
                  onValueChange={(next) => {
                    rhfField.onChange({
                      fieldId: field.id,
                      value: { select: next },
                    });
                    if (next) {
                      form.clearErrors(`customFields.${index}`);
                    }
                  }}
                >
                  <SelectTrigger
                    style={{
                      width: '100%',
                      background: 'transparent',
                      border: 'none',
                      padding: 0,
                      height: 'auto',
                      color: t.foreground,
                      fontFamily: t.fontSans,
                      fontSize: 16,
                    }}
                  >
                    <SelectValue placeholder={translate('common:selectPlaceholder')} />
                  </SelectTrigger>
                  <SelectContent {...presentation}>
                    {(field.options ?? []).map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </NativeInputRow>
            );
          }}
        />
      );

    case 'boolean':
      return (
        <FormField
          control={form.control}
          name={`customFields.${index}`}
          render={({ field: rhfField, fieldState }) => {
            const stored = rhfField.value as
              | { value?: { boolean?: boolean } }
              | undefined;
            const checked = stored?.value?.boolean ?? false;
            const hasError = Boolean(fieldState.error);
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 16,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 4,
                      flex: 1,
                    }}
                  >
                    <span
                      style={{
                        fontFamily: t.fontSans,
                        fontSize: 14,
                        fontWeight: 500,
                        lineHeight: 1.3,
                        color: hasError ? '#B25040' : t.foreground,
                      }}
                    >
                      {label}
                    </span>
                    {description ? (
                      <span
                        style={{
                          fontFamily: t.fontSans,
                          fontSize: 12,
                          color: t.muted,
                          lineHeight: 1.4,
                        }}
                      >
                        {description}
                      </span>
                    ) : null}
                  </div>
                  <Switch
                    disabled={disabled}
                    checked={checked}
                    onCheckedChange={(next) => {
                      rhfField.onChange({
                        fieldId: field.id,
                        value: { boolean: next },
                      });
                      form.clearErrors(`customFields.${index}`);
                    }}
                  />
                </div>
                {fieldState.error?.message ? (
                  <span
                    style={{
                      fontFamily: t.fontSans,
                      fontSize: 12,
                      color: '#B25040',
                    }}
                  >
                    {fieldState.error.message}
                  </span>
                ) : null}
              </div>
            );
          }}
        />
      );

    case 'file':
      return (
        <FormField
          control={form.control}
          name={`customFields.${index}`}
          render={({ fieldState }) => (
            <FileDropzone
              fieldLabel={field.label}
              isRequired={field.is_required}
              description={description}
              selectedFileName={selectedFileName}
              disabled={disabled}
              error={fieldState.error?.message}
              onSelect={(file) => {
                pendingFiles.current.set(field.id, file);
                setSelectedFileName(file.name);
                form.setValue(`customFields.${index}`, {
                  fieldId: field.id,
                  value: { file: 'pending' },
                });
                form.clearErrors(`customFields.${index}`);
              }}
              onClear={() => {
                setSelectedFileName(null);
                pendingFiles.current.delete(field.id);
                form.setValue(`customFields.${index}`, {
                  fieldId: field.id,
                  value: { file: '' },
                });
              }}
            />
          )}
        />
      );

    case 'tour':
      return (
        <FormField
          control={form.control}
          name={`customFields.${index}`}
          render={({ field: rhfField, fieldState }) => {
            const stored = rhfField.value as
              | { value?: { tour?: TourSelection } }
              | undefined;
            return (
              <IntakeTourField
                field={field}
                disabled={disabled}
                error={fieldState.error?.message}
                value={stored?.value?.tour}
                onChange={(tour) =>
                  rhfField.onChange(
                    tour ? { fieldId: field.id, value: { tour } } : undefined,
                  )
                }
              />
            );
          }}
        />
      );

    default:
      return null;
  }
}

/**
 * Native-styled file dropzone. Lives outside the grouped panel because file
 * uploads need a larger touch target and a "drop / browse" affordance that
 * doesn't fit the single-row idiom.
 */
function FileDropzone({
  fieldLabel,
  isRequired,
  description,
  selectedFileName,
  disabled,
  error,
  onSelect,
  onClear,
}: {
  fieldLabel: string;
  isRequired: boolean;
  description?: string;
  selectedFileName: string | null;
  disabled: boolean;
  error?: string;
  onSelect: (file: File) => void;
  onClear: () => void;
}) {
  const t = useNativeTokens();
  const { t: translate } = useTranslation();
  const hasError = Boolean(error);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span
        style={{
          fontFamily: t.fontSans,
          fontSize: 14,
          fontWeight: 500,
          lineHeight: 1.3,
          color: hasError ? '#B25040' : t.foreground,
        }}
      >
        {fieldLabel}
        {isRequired ? ' *' : ''}
      </span>
      {description ? (
        <span
          style={{
            fontFamily: t.fontSans,
            fontSize: 12,
            color: t.muted,
            lineHeight: 1.4,
          }}
        >
          {description}
        </span>
      ) : null}
      {selectedFileName ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '14px 16px',
            borderRadius: t.radius,
            border: `1px solid ${t.foreground}1F`,
            background: isDarkBg(t.background)
              ? 'rgba(255,255,255,0.05)'
              : 'rgba(0,0,0,0.035)',
          }}
        >
          <FileText
            size={16}
            strokeWidth={1.6}
            style={{ color: t.muted, flexShrink: 0 }}
          />
          <span
            style={{
              flex: 1,
              fontFamily: t.fontSans,
              fontSize: 13,
              color: t.foreground,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {selectedFileName}
          </span>
          <button
            type="button"
            disabled={disabled}
            onClick={onClear}
            style={{
              padding: 4,
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: t.muted,
              borderRadius: 999,
            }}
            aria-label={translate('common:removeFile')}
          >
            <X size={14} strokeWidth={1.6} />
          </button>
        </div>
      ) : (
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            padding: '22px 16px',
            borderRadius: t.radius,
            border: `1.5px dashed ${hasError ? '#B25040' : `${t.foreground}40`}`,
            background: isDarkBg(t.background)
              ? 'rgba(255,255,255,0.04)'
              : 'rgba(0,0,0,0.025)',
            color: t.foreground,
            cursor: disabled ? 'not-allowed' : 'pointer',
            fontFamily: t.fontSans,
            fontSize: 14,
            opacity: disabled ? 0.5 : 1,
          }}
        >
          <Upload size={16} strokeWidth={1.6} />
          <span>
            <Trans
              i18nKey="marketing:chooseFileHint"
              defaults="Choose a file (PDF, image, or document, max 4MB)"
            />
          </span>
          <input
            type="file"
            className="sr-only"
            accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx"
            disabled={disabled}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;

              // Validate MIME type client-side to give immediate feedback.
              // The Supabase bucket also enforces this server-side, but early
              // rejection avoids a wasted round-trip for the signed URL request.
              if (!ALLOWED_MIME_TYPES.has(file.type)) {
                toast.error(
                  translate('marketing:fileTypeNotSupported', {
                    defaultValue:
                      'File type not supported. Please upload a PDF, image, or Word document.',
                  }),
                );
                e.target.value = '';
                return;
              }

              if (file.size > 4 * 1024 * 1024) {
                toast.error(
                  translate('marketing:fileTooLarge', {
                    defaultValue: 'File too large (max 4MB)',
                  }),
                );
                e.target.value = '';
                return;
              }

              onSelect(file);
            }}
          />
        </label>
      )}
      {error ? (
        <span
          style={{
            fontFamily: t.fontSans,
            fontSize: 12,
            color: '#B25040',
            marginTop: 2,
          }}
        >
          {error}
        </span>
      ) : null}
    </div>
  );
}
